// GM-owned FT3D session mode. The durable value rides the canonical campaign_arr and therefore
// reaches current clients through campaign_update while also seeding late joiners on page load.

import type { CampaignArr, CampaignFt3dMode } from '../shared/array-types';

type Ft3dSessionMode = CampaignFt3dMode;

function ftNormaliseFt3dSessionMode(value: unknown): Ft3dSessionMode {
    const mode = String(value ?? '').trim().toLowerCase();
    return mode === 'paused' || mode === 'theatre' ? mode : 'active';
}

function ftCampaignModeField(campaignArr: unknown): Ft3dSessionMode {
    if (!campaignArr || typeof campaignArr !== 'object') return 'active';
    const arr = campaignArr as CampaignArr;
    return ftNormaliseFt3dSessionMode(arr.campaigns?.campaign_ft3d_mode ?? arr.campaign_ft3d_mode);
}

function ftSyncFt3dSessionModeBannerLayout(rootOverride?: HTMLElement | null): void {
    const root = rootOverride
        ?? document.querySelector<HTMLElement>('[data-ft3d-campaign-encounter-view]');
    if (!root) return;

    const gmWindow = root.querySelector<HTMLElement>('[data-ft3d-window="gm"]');
    const mode = ftNormaliseFt3dSessionMode(root.dataset.ft3dSessionMode);
    const activeBanner = mode === 'active'
        ? null
        : root.querySelector<HTMLElement>(`[data-ft3d-session-mode-banner="${mode}"]:not([hidden])`);
    const docksBelowGm = root.dataset.ft3dCanManage === '1'
        && activeBanner !== null
        && gmWindow !== null
        && !gmWindow.hidden;

    root.classList.toggle('has-ft3d-session-mode-banner-under-gm', docksBelowGm);
    if (!docksBelowGm || !gmWindow) {
        root.style.removeProperty('--ft3d-session-banner-top');
        return;
    }

    const rootRect = root.getBoundingClientRect();
    const gmRect = gmWindow.getBoundingClientRect();
    root.style.setProperty(
        '--ft3d-session-banner-top',
        `${Math.max(0, Math.round(gmRect.bottom - rootRect.top) - 1)}px`,
    );
}

function ftApplyFt3dSessionMode(value: unknown, source = 'unknown'): Ft3dSessionMode {
    const mode = ftNormaliseFt3dSessionMode(value);
    const root = document.querySelector<HTMLElement>('[data-ft3d-campaign-encounter-view]');
    if (!root) return mode;

    const previous = ftNormaliseFt3dSessionMode(root.dataset.ft3dSessionMode);
    root.dataset.ft3dSessionMode = mode;
    root.setAttribute('data-ft3d-session-mode-source', source);

    const isGm = root.dataset.ft3dCanManage === '1';
    const playerLocked = !isGm && mode === 'paused';
    document.body.classList.toggle('ft3d-session-mode-player-locked', playerLocked);

    root.querySelectorAll<HTMLElement>('[data-ft3d-session-mode-overlay]').forEach((overlay) => {
        const overlayMode = ftNormaliseFt3dSessionMode(overlay.dataset.ft3dSessionModeOverlay);
        const visible = !isGm && mode === 'paused' && overlayMode === 'paused';
        overlay.hidden = !visible;
        overlay.setAttribute('aria-hidden', visible ? 'false' : 'true');
        if (visible) overlay.tabIndex = -1;
    });

    // Theatre is a soft campaign-state notice for everyone. Hard Pause gives players the blocking
    // overlay above, while the GM retains director controls and receives the persistent banner.
    root.querySelectorAll<HTMLElement>('[data-ft3d-session-mode-banner]').forEach((banner) => {
        const bannerMode = ftNormaliseFt3dSessionMode(banner.dataset.ft3dSessionModeBanner);
        const visible = mode === bannerMode && (isGm || bannerMode === 'theatre');
        banner.hidden = !visible;
        banner.setAttribute('aria-hidden', visible ? 'false' : 'true');
    });
    ftSyncFt3dSessionModeBannerLayout(root);

    root.querySelectorAll<HTMLElement>('[data-ft3d-session-mode-control]').forEach((button) => {
        const selected = ftNormaliseFt3dSessionMode(button.dataset.ft3dSessionMode) === mode;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    const visibleOverlay = mode === 'paused'
        ? root.querySelector<HTMLElement>('[data-ft3d-session-mode-overlay="paused"]:not([hidden])')
        : null;
    if (mode !== previous && visibleOverlay !== null) {
        visibleOverlay.focus({ preventScroll: true });
    }

    // ⛔ No campaign-clock write here. This function is PRESENTATION: it also runs from the incoming
    // campaign_update frame and from the page-load restore, so a clock command placed here fires from
    // every GM tab for a mode change one of them already made — and never fires at all for a repeat
    // click, because of the mode !== previous gate. The session-mode transport owns that coupling in
    // exactly one place: _ftTwgSetCampaignClockRunning in turn-widget-gm-controls.ts, on the gesture.

    if (!isGm && mode !== previous) {
        if (mode === 'paused') window.displayWarning?.('The GM has paused play.');
        else if (mode === 'theatre') window.displayWarning?.('The GM has entered Theatre of the Mind. Play remains interactive.');
        else if (previous !== 'active') window.displayWarning?.('The GM has resumed play.');
    }

    window.dispatchEvent(new CustomEvent('ft:ft3d-session-mode-changed', {
        detail: { mode, previous, source, playerLocked },
    }));
    return mode;
}

function ftCurrentFt3dSessionMode(): Ft3dSessionMode {
    const root = document.querySelector<HTMLElement>('[data-ft3d-campaign-encounter-view]');
    return ftNormaliseFt3dSessionMode(root?.dataset.ft3dSessionMode);
}

// Only Hard Pause owns player input. Theatre is deliberately presentation-only: camp play, scene
// controls, movement, sheets, inventory drag/drop, and party tools all remain interactive.
function blockPausedPlayerEvent(event: Event): void {
    const root = document.querySelector<HTMLElement>('[data-ft3d-campaign-encounter-view]');
    if (!root || root.dataset.ft3dCanManage === '1') return;
    const mode = ftCurrentFt3dSessionMode();
    if (mode !== 'paused') return;
    // The party-vote panel is social coordination, not play — "should we stop for the night?" is
    // exactly the question a GM asks WHILE paused, so a hard pause must not swallow the ballot.
    if (event.target instanceof Element && event.target.closest('[data-ft3d-party-vote]')) return;
    // Attendance is out-of-game coordination, like a party vote. Hard Pause must not
    // suppress the ready click and let settlement remove a player who is at the table.
    if (event.target instanceof Element && event.target.closest('#campaignSessionReadyModal, #campaignPopup')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
}

for (const eventName of ['pointerdown', 'mousedown', 'touchstart', 'click', 'dblclick', 'wheel', 'submit']) {
    document.addEventListener(eventName, blockPausedPlayerEvent, { capture: true, passive: false });
}
document.addEventListener('keydown', blockPausedPlayerEvent, true);

window.addEventListener('ft:campaign-state-received', (event) => {
    const detail = (event as CustomEvent<{ campaignArr?: unknown }>).detail;
    ftApplyFt3dSessionMode(ftCampaignModeField(detail?.campaignArr), 'campaign-update');
});

function ftRestoreFt3dSessionModeFromPage(source = 'page-load'): boolean {
    const root = document.querySelector<HTMLElement>('[data-ft3d-campaign-encounter-view]');
    if (!root) return false;
    ftApplyFt3dSessionMode(root.dataset.ft3dSessionMode, source);
    return true;
}

// The campaign surface is parsed before the footer bundle on my_campaign.php, so restore its
// persisted mode immediately. Keep DOMContentLoaded only for pages that load this bundle earlier.
// This also makes dynamically/instrumentally loaded bundles safe after DOMContentLoaded has fired.
if (!ftRestoreFt3dSessionModeFromPage() && document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        ftRestoreFt3dSessionModeFromPage();
    }, { once: true });
}

declare global {
    interface Window {
        ftApplyFt3dSessionMode?: typeof ftApplyFt3dSessionMode;
        ftCurrentFt3dSessionMode?: typeof ftCurrentFt3dSessionMode;
        ftRestoreFt3dSessionModeFromPage?: typeof ftRestoreFt3dSessionModeFromPage;
        ftSyncFt3dSessionModeBannerLayout?: typeof ftSyncFt3dSessionModeBannerLayout;
    }
}

window.ftApplyFt3dSessionMode = ftApplyFt3dSessionMode;
window.ftCurrentFt3dSessionMode = ftCurrentFt3dSessionMode;
window.ftRestoreFt3dSessionModeFromPage = ftRestoreFt3dSessionModeFromPage;
window.ftSyncFt3dSessionModeBannerLayout = ftSyncFt3dSessionModeBannerLayout;

export {
    ftApplyFt3dSessionMode,
    ftCurrentFt3dSessionMode,
    ftNormaliseFt3dSessionMode,
    ftRestoreFt3dSessionModeFromPage,
    ftSyncFt3dSessionModeBannerLayout,
};
